import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, ZapIcon, AlertTriangleIcon, FlameIcon } from 'lucide-react';
import { FloatingElements } from './FloatingElements';
import { useGeoPrice } from '../hooks/useGeoPrice';
function CountdownTimer() {
  const [timeLeft, setTimeLeft] = useState(() => {
    const saved = localStorage.getItem('las3jarras_countdown_end');
    if (saved) {
      const remaining = Math.max(0, Math.floor((parseInt(saved) - Date.now()) / 1000));
      if (remaining > 0) return remaining;
    }
    const end = Date.now() + 48 * 60 * 60 * 1000;
    localStorage.setItem('las3jarras_countdown_end', end.toString());
    return 48 * 60 * 60;
  });
  useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor(timeLeft % 3600 / 60);
  const seconds = timeLeft % 60;
  return <div className="flex items-center justify-center gap-2 md:gap-3" data-id="element-168">
      <div className="flex flex-col items-center" data-id="element-169">
        <span className="font-display text-3xl md:text-4xl font-extrabold text-white tabular-nums bg-black/40 rounded-lg px-3 py-1.5 min-w-[3.5rem] text-center border border-urgency/30" data-id="element-170">
          {String(hours).padStart(2, '0')}
        </span>
        <span className="text-[10px] text-white/40 mt-1 uppercase tracking-wider font-display" data-id="element-171">
          Horas
        </span>
      </div>
      <span className="font-display text-2xl md:text-3xl font-bold text-urgency mb-4 urgency-pulse" data-id="element-172">
        :
      </span>
      <div className="flex flex-col items-center" data-id="element-173">
        <span className="font-display text-3xl md:text-4xl font-extrabold text-white tabular-nums bg-black/40 rounded-lg px-3 py-1.5 min-w-[3.5rem] text-center border border-urgency/30" data-id="element-174">
          {String(minutes).padStart(2, '0')}
        </span>
        <span className="text-[10px] text-white/40 mt-1 uppercase tracking-wider font-display" data-id="element-175">
          Min
        </span>
      </div>
      <span className="font-display text-2xl md:text-3xl font-bold text-urgency mb-4 urgency-pulse" data-id="element-176">
        :
      </span>
      <div className="flex flex-col items-center" data-id="element-177">
        <span className="font-display text-3xl md:text-4xl font-extrabold text-white tabular-nums bg-black/40 rounded-lg px-3 py-1.5 min-w-[3.5rem] text-center border border-urgency/30" data-id="element-178">
          {String(seconds).padStart(2, '0')}
        </span>
        <span className="text-[10px] text-white/40 mt-1 uppercase tracking-wider font-display" data-id="element-179">
          Seg
        </span>
      </div>
    </div>;
}
export function Pricing() {
  const {
    symbol,
    offerPrice,
    originalPrice,
    label
  } = useGeoPrice();
  return <section id="precio" className="bg-dark py-20 md:py-32 px-5 relative overflow-hidden" data-id="element-180">
      <div className="absolute inset-0 bg-radial-money" aria-hidden="true" data-id="element-181" />
      <FloatingElements count={6} variant="money" data-id="element-182" />

      <div className="max-w-lg mx-auto relative z-10" data-id="element-183">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} viewport={{
        once: true,
        margin: '-80px'
      }} data-id="element-184">
          {/* URGENCY BANNER */}
          <div className="bg-urgency text-white text-center py-3 px-4 rounded-t-3xl font-display font-extrabold text-sm md:text-base uppercase tracking-wider flex items-center justify-center gap-2 animate-subtle-scale" data-id="element-185">
            <FlameIcon className="w-5 h-5" data-id="element-186" />
            🔥 SUPER PROMO — SOLO ESTA SEMANA 🔥
            <FlameIcon className="w-5 h-5" data-id="element-187" />
          </div>

          {/* Pricing card */}
          <a href="https://pay.hotmart.com/R104423897O" target="_blank" rel="noopener noreferrer" className="block bg-dark-card border-2 border-urgency/40 rounded-b-3xl overflow-hidden cursor-pointer transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99]" style={{
          boxShadow: '0 0 50px rgba(220, 38, 38, 0.15), 0 0 100px rgba(0, 200, 83, 0.1)'
        }} data-id="element-188">
            <div className="p-8 md:p-10" data-id="element-189">
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-white text-center mb-1" data-id="element-190">
                Las 3 Jarras
              </h2>
              <p className="font-sans text-white/30 text-center text-sm mb-6" data-id="element-191">
                El Sistema Familiar
              </p>

              {/* Discount badge */}
              <div className="flex justify-center mb-6" data-id="element-192">
                <div className="bg-urgency text-white font-display font-extrabold text-lg md:text-xl px-6 py-2 rounded-full shadow-lg animate-gentle-rotate" style={{
                boxShadow: '0 0 20px rgba(220, 38, 38, 0.4)'
              }} data-id="element-193">
                  🏷️ 88% DE DESCUENTO
                </div>
              </div>

              {/* Price comparison */}
              <div className="text-center mb-2" data-id="element-194">
                <p className="font-sans text-white/40 text-xs uppercase tracking-wider mb-2" data-id="element-195">
                  Precio regular
                </p>
                <div className="flex items-center justify-center gap-3 mb-2" data-id="element-196">
                  <span className="font-display text-white/30 text-3xl md:text-4xl font-bold line-through decoration-urgency decoration-[3px]" data-id="element-197">
                    {symbol}
                    {originalPrice} {label}
                  </span>
                </div>
                <p className="font-sans text-money text-xs uppercase tracking-wider mb-1" data-id="element-198">
                  Precio de esta semana
                </p>
                <div className="animate-price-breathe" data-id="element-199">
                  <span className={`font-display font-extrabold shimmer-text ${offerPrice.length > 5 ? 'text-5xl md:text-6xl' : 'text-7xl md:text-8xl'}`} data-id="element-200">
                    {symbol}
                    {offerPrice}
                  </span>
                </div>
                <p className="font-display text-money font-bold text-sm mt-1" data-id="element-201">
                  {label} — ¡Aprovecha antes de que suba!
                </p>
              </div>

              {/* Countdown */}
              <div className="my-8 py-5 px-4 bg-black/30 rounded-2xl border border-urgency/20" data-id="element-202">
                <div className="flex items-center justify-center gap-2 mb-4" data-id="element-203">
                  <AlertTriangleIcon className="w-4 h-4 text-urgency" data-id="element-204" />
                  <p className="font-display text-urgency font-bold text-sm uppercase tracking-wider urgency-pulse" data-id="element-205">
                    Esta promo se acaba en:
                  </p>
                </div>
                <CountdownTimer data-id="element-206" />
                <p className="text-center text-white/25 text-xs mt-3 font-sans" data-id="element-207">
                  Después el precio vuelve a {symbol}
                  {originalPrice} {label}
                </p>
              </div>

              {/* CTA */}
              <div className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-money-dark via-money to-emerald-400 text-white font-display font-extrabold text-lg md:text-xl px-8 py-6 rounded-2xl cta-pulse mb-5 animate-subtle-scale" data-id="element-208">
                🔓 OBTENER EL SISTEMA — {symbol}
                {offerPrice} {label}
              </div>

              {/* Urgency badge */}
              <div className="flex items-center justify-center gap-2 text-money text-sm font-display font-bold mb-4" data-id="element-209">
                <ZapIcon className="w-4 h-4" data-id="element-210" />
                Descarga inmediata — empieza HOY
              </div>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-4 text-white/30 text-xs font-sans" data-id="element-211">
                <span className="flex items-center gap-1" data-id="element-212">
                  <DownloadIcon className="w-3 h-3" data-id="element-213" /> PDF + Plantillas
                </span>
                <span data-id="element-214">·</span>
                <span data-id="element-215">Niños de 4 a 14 años</span>
              </div>
            </div>
          </a>
        </motion.div>

        {/* Final push */}
        <motion.div className="mt-14 text-center" initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} viewport={{
        once: true,
        margin: '-40px'
      }} data-id="element-216">
          <p className="font-serif italic text-white/35 text-lg leading-relaxed mb-6" data-id="element-217">
            Si hoy tu casa funciona con improvisación, mañana será más difícil
            corregirlo. No porque seas mal padre/madre. Sino porque el patrón se
            consolida.
          </p>
          <p className="font-display text-white text-xl font-bold mb-6" data-id="element-218">
            Instala el ritual. Hazlo simple. Hazlo repetible.
          </p>
          <div className="w-16 h-px bg-money/20 mx-auto mb-6" data-id="element-219" />
          <p className="font-sans text-white/30 text-sm leading-relaxed max-w-md mx-auto" data-id="element-220">
            <span className="font-display font-bold text-money" data-id="element-221">
              Las 3 Jarras
            </span>
            : 7 minutos por semana para que el dinero deje de ser pleito… y se
            convierta en <span className="text-money" data-id="element-222">formación</span>.
          </p>
        </motion.div>
      </div>
    </section>;
}